import { useState, ChangeEvent } from "react";
import MsTable from "../../../components/Widgets/Table";
import ExploreCategoryModal from "./ExploreCategoryModal";
import { FaTrash } from "react-icons/fa";
import { colType } from "../../../Types";
import PageHeader from "components/PageHeader";
import useExploreCategoriesQuery from "../hooks/useExploreCategoriesQuery";
import useAddExploreCategoryMutation from "../hooks/useAddExploreCategoryMutation";
import useRemoveExploreCategoryMutation from "../hooks/useRemoveExploreCategoryMutation";
import useUpdateExploreCategoryMutation from "../hooks/useUpdateExploreCategoryMutation";
import { ExploreCategory } from "../../../api/explore-categories";
import uploadFile from "../../../utils/uploadFiles";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSAlert from "../../system-design/DSAlert";
import DSStack from "../../system-design/DSStack";
import DSTextField from "../../system-design/DSTextField";
import DSUploadButton from "../../system-design/DSUploadButton";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";

export type ExploreCategoryRow = {
  index: JSX.Element;
  name: JSX.Element;
  logo: JSX.Element;
  description: string;
  numKlydos: number;
  active: JSX.Element;
  delete: JSX.Element;
};

function ExploreCategories() {
  const { data: exploreCategories } = useExploreCategoriesQuery();
  const {
    mutate: addExploreCategoryMutation,
    isPending: isAddingExploreCategory,
  } = useAddExploreCategoryMutation();
  const { mutate: removeExploreCategoryMutation } =
    useRemoveExploreCategoryMutation();
  const { mutate: updateExploreCategoryMutation } =
    useUpdateExploreCategoryMutation();
  const [showAddExploreCategory, setShowAddExploreCategory] =
    useState<boolean>(false);
  const [exploreCategoryToDelete, setExploreCategoryToDelete] = useState("");

  const tableColumns: Array<colType> = [
    { label: "index", field: "index", sort: true },
    { label: "name", field: "name", sort: true },
    { label: "logo", field: "logo", sort: false },
    { label: "description", field: "description", sort: false },
    { label: "num klydos", field: "numKlydos", sort: true },
    { label: "active", field: "active", sort: true },
    {
      size: 32,
      noHeadline: true,
      sort: false,
      label: "",
      field: "delete",
    },
  ];

  const onIndexChange = (id: string, displayedIndex: number) => {
    if (isNaN(displayedIndex)) return;
    const newIndex = displayedIndex - 1;
    const currentIndex = exploreCategories!.find((p) => p.id === id)!.index;
    const categoriesAndIndex = exploreCategories!.map((category) => ({
      id: category.id,
      index: category.index,
    }));
    const [movedItem] = categoriesAndIndex.splice(currentIndex, 1);
    categoriesAndIndex.splice(newIndex, 0, movedItem);
    categoriesAndIndex.forEach((item, index) => {
      item.index = index;
    });
    const changedCategories = categoriesAndIndex.filter(
      (category) =>
        exploreCategories?.find((p) => p.id === category.id)?.index !==
        category.index,
    );
    changedCategories.forEach((category) => {
      updateExploreCategoryMutation({
        id: category.id,
        index: category.index,
      });
    });
  };

  const tableRows = (exploreCategory: ExploreCategory): ExploreCategoryRow => {
    return {
      index: (
        <input
          key={exploreCategory.index}
          defaultValue={exploreCategory.index}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onIndexChange(exploreCategory.id, +e.target.value)}
          style={{ width: "60px" }}
        />
      ),
      name: (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {exploreCategory.name}
          {exploreCategory.autoFill && (
            <DSChip label="Auto" size="small" color="info" variant="outlined" />
          )}
        </span>
      ),
      logo: exploreCategory.logoUrl ? (
        <img
          width={50}
          src={exploreCategory.logoUrl}
          alt={exploreCategory.name}
          style={{ backgroundColor: "#333", borderRadius: 8, padding: 4 }}
        />
      ) : (
        <DSTypography sx={{ textAlign: "center" }}>No logo</DSTypography>
      ),
      description: exploreCategory.description || "",
      numKlydos: exploreCategory.klydos?.length || 0,
      active: (
        <DSTypography
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            color: exploreCategory.active ? "green" : "red",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          {exploreCategory.active ? "Active" : "Inactive"}
        </DSTypography>
      ),
      delete: (
        <FaTrash
          style={{
            margin: "8px",
            opacity: exploreCategory.active ? 0.3 : 1,
            cursor: exploreCategory.active ? "not-allowed" : "pointer",
          }}
          title={
            exploreCategory.active
              ? "Cannot delete an active category"
              : "Delete category"
          }
          className="nc-icon nc-simple-remove"
          onClick={(e) => {
            e.stopPropagation();
            if (exploreCategory.active) return;
            setExploreCategoryToDelete(exploreCategory.id);
          }}
        />
      ),
    };
  };

  const DeleteExploreCategoryModal = () => {
    return (
      <>
        {exploreCategoryToDelete && (
          <DSDialog open onClose={() => setExploreCategoryToDelete("")}>
            <DSDialogTitle>Confirm Deletion</DSDialogTitle>
            <DSDialogContent>
              Are you sure you want to delete this explore category?
            </DSDialogContent>
            <DSDialogActions>
              <DSSecondaryButton onClick={() => setExploreCategoryToDelete("")}>
                Cancel
              </DSSecondaryButton>
              <DSErrorButton
                onClick={() => {
                  removeExploreCategoryMutation(exploreCategoryToDelete);
                  setExploreCategoryToDelete("");
                }}
              >
                Delete
              </DSErrorButton>
            </DSDialogActions>
          </DSDialog>
        )}
      </>
    );
  };

  const AddNewExploreCategoryModal = () => {
    const [newExploreCategoryName, setNewExploreCategoryName] = useState("");
    const [newExploreCategoryLogo, setNewExploreCategoryLogo] = useState("");
    const [newExploreCategoryDescription, setNewExploreCategoryDescription] =
      useState("");
    const [showWarning, setShowWarning] = useState(false);

    const addNewExploreCategory = async () => {
      if (!newExploreCategoryName) {
        setShowWarning(true);
        return;
      }
      setShowWarning(false);
      addExploreCategoryMutation(
        {
          name: newExploreCategoryName,
          logoUrl: newExploreCategoryLogo,
          description: newExploreCategoryDescription,
          active: false,
          index: exploreCategories?.length || 0,
        },
        {
          onSuccess: () => setShowAddExploreCategory(false),
        },
      );
    };

    return (
      <>
        <DSDialogTitle>Add New Explore Category</DSDialogTitle>
        <DSDialogContent>
          {showWarning && (
            <DSAlert severity="error" sx={{ mb: 2 }}>
              Explore Category Name is required!
            </DSAlert>
          )}
          <form>
            <DSStack spacing={2} sx={{ mt: 1 }}>
              <DSTextField
                id="formExploreCategoryName"
                label="Explore Category Name"
                type="text"
                fullWidth
                placeholder="Enter explore category name"
                value={newExploreCategoryName}
                onChange={(e) => setNewExploreCategoryName(e.target.value)}
              />
              <DSTextField
                id="formExploreCategoryDescription"
                label="Description"
                multiline
                rows={2}
                fullWidth
                placeholder="Enter description"
                value={newExploreCategoryDescription}
                onChange={(e) =>
                  setNewExploreCategoryDescription(e.target.value)
                }
              />
              <label style={{ width: "100%" }}>
                <input
                  id="formExploreCategoryLogo"
                  type="file"
                  accept="image/png"
                  hidden
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const fileReader = new FileReader();
                      fileReader.onload = () => {
                        uploadFile({
                          file: fileReader.result as string,
                          folder: "explore-category-logo",
                          upload_preset: "collection_logo",
                        }).then((url) => setNewExploreCategoryLogo(url));
                      };
                      fileReader.readAsDataURL(file);
                    }
                  }}
                />
                <DSUploadButton
                  text={newExploreCategoryLogo ? "Logo uploaded" : "Logo"}
                />
              </label>
            </DSStack>
          </form>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setShowAddExploreCategory(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            disabled={isAddingExploreCategory}
            onClick={() => {
              addNewExploreCategory();
            }}
          >
            Add Explore Category
          </DSPrimaryButton>
        </DSDialogActions>
      </>
    );
  };

  return (
    <>
      <PageHeader
        overline="Content"
        title="Explore"
        subtitle="Curate the categories shown in the app's explore feed."
        actions={
          <DSPrimaryButton onClick={() => setShowAddExploreCategory(true)}>
            Add Category
          </DSPrimaryButton>
        }
      />
      {exploreCategories && (
        <>
          <MsTable
            id="ExploreCategories"
            data={[...exploreCategories]
              .sort(
                (firstCategory, secondCategory) =>
                  firstCategory.index - secondCategory.index,
              )
              .map((category) => ({
                ...category,
                numKlydos: category.klydos?.length || 0,
              }))}
            cols={tableColumns}
            rowBuild={tableRows}
            title="Explore Categories"
            search={(row: ExploreCategory, val: string) => {
              return row.name.toLowerCase().includes(val.toLowerCase());
            }}
            modal={ExploreCategoryModal}
          />
        </>
      )}
      <DeleteExploreCategoryModal />
      {showAddExploreCategory && (
        <DSDialog
          open={true}
          maxWidth="md"
          fullWidth
          onClose={() => setShowAddExploreCategory(false)}
        >
          <AddNewExploreCategoryModal />
        </DSDialog>
      )}
    </>
  );
}

export default ExploreCategories;
